import React from 'react';
import { Box } from '@chakra-ui/react';
import type { ScorecardThresholdFieldProps } from './ScorecardThresholdField.interface';
import { ScorecardThresholdSelector } from './ScorecardThresholdSelector';

const ROLE_GROUP_IDS = [
  'job_coordinator',
  'job_recruiter',
  'job_hiring_manager',
];

/**
 * Drop-in FormBuilder custom Field for the scorecard completion threshold.
 * Handles the role-group detection logic in one place so every form
 * (assessment, review, coding-assessment) behaves consistently.
 *
 * Gate inclusion with `feScorecardThreshold` at each FormBuilder config.
 *
 * Usage in a FormBuilder config:
 *   { name: 'scorecard_completion_threshold', type: 'custom', Field: ScorecardThresholdField }
 *
 * By default it reads `formik.values.reviewer_ids`.
 * Pass reviewerIdsKey="participant_ids" for review forms that use that field name.
 */
export const ScorecardThresholdField: React.FC<
  ScorecardThresholdFieldProps
> = ({
  formik,
  reviewerIdsKey = 'reviewer_ids',
  useCriteriaCorpSpacing = false,
}) => {
  const reviewerIds: (string | number)[] = formik.values[reviewerIdsKey] ?? [];
  const hasGroupReviewers = reviewerIds.some((id) =>
    ROLE_GROUP_IDS.includes(String(id))
  );

  return (
    <Box
      width="100%"
      {...(useCriteriaCorpSpacing
        ? { px: '6', sx: { margin: '1em 0' } }
        : { paddingLeft: '24px', paddingRight: '24px' })}
    >
      <ScorecardThresholdSelector
        value={formik.values.scorecard_completion_threshold}
        onChange={(val) =>
          formik.setFieldValue('scorecard_completion_threshold', val)
        }
        reviewerCount={reviewerIds.length}
        hasGroupReviewers={hasGroupReviewers}
      />
    </Box>
  );
};
